import React, { useState, useEffect, useRef } from 'react';
import { 
  Save, Plus, Trash2, Star, Check,
  Building2, MapPin, Home, Wifi, CreditCard, ClipboardCheck,
  ChevronLeft, ChevronRight, ChevronDown, Loader2, Upload, X, QrCode, ZoomIn, FileText
} from 'lucide-react';
import { getCitiesForState } from '../constants/indianCities';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// ==================== CONSTANTS ====================

const PROPERTY_TYPES = [
  'Apartment', 'Bed & Breakfast', 'Boutique Hotel', 'Camp', 'Cottage',
  'Farmhouse', 'Guest House', 'Home Stay', 'Hostel', 'Hotel',
  'Lodge', 'Resort', 'Service Apartment', 'Villa'
];

const ROOM_CATEGORIES = ['Standard Room', 'Deluxe Room', 'Double Room', 'Single Room', 'Triple Room', 'Family Room', 'Suite', 'Other'];
const ROOM_TYPE_CATEGORIES = ['AC', 'Non-AC'];

const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
  'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
  'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Delhi', 'Jammu and Kashmir', 'Ladakh'
];

// Mega-Spec Item 4 (Step 4) — room-type-specific amenities. Bathroom requires
// a Private/Shared sub-choice; no other amenity has any sub-option.
const ROOM_AMENITIES = [
  'TV', 'Fan', 'Cooler', 'Wifi', 'Cupboard', 'Sofa',
  'Bathroom', 'Jacuzzi', 'Bathtub', 'Toiletries', 'Mirror'
];
const BATHROOM_OPTIONS = ['Private', 'Shared'];

// Property Facilities & Policies (Step 4). Three groups:
//  • YES_NO_FACILITIES — explicit Yes/No, mandatory selection.
//  • CHARGE_FACILITIES — optional toggle; when enabled choose Free/Paid
//    (Paid → amount). Swimming Pool also needs Indoor/Outdoor.
//  • TOGGLE_FACILITIES — simple on/off.
const YES_NO_FACILITIES = [
  'Couple Friendly',
  'Unmarried Couples Allowed',
  'Outside Food Allowed',
  'Non-Veg Food Allowed',
  'Alcohol Allowed',
  'Smoking Allowed Inside Room',
  'Local ID Allowed',
  'Foreigners Allowed',
  'Pet Allowed',
];
const CHARGE_FACILITIES = ['Parking', 'Room Heater', 'Breakfast', 'Swimming Pool'];
const TOGGLE_FACILITIES = [
  'Security Camera',
  'Luggage Storage',
  'Outdoor Dining Area',
  'Restaurant',
  'Kitchen',
  '24 x 7 Reception',
  'Room Service',
  'Power Backup',
  'Banquet Hall',
  'Conference Room',
];

// Legacy camelCase key → display-label mapping for amenities
const AMENITY_KEY_MAP = {
  'wifi': 'Wifi', 'ac': 'Air Conditioning', 'reception': 'Reception', 'tv': 'TV',
  'roomService': 'Room Service', 'elevator': 'Elevator', 'powerBackup': 'Power Backup',
  'parking': 'Parking', 'breakfast': 'Breakfast', 'roomHeater': 'Room Heater',
  'smokingRoom': 'Smoking Room', 'fan': 'Fan', 'swimmingPool': 'Swimming Pool',
  'coupleFriendly': 'Couple Friendly', 'alcoholAllowed': 'Alcohol Allowed',
  'housekeeping': 'Housekeeping'
};

// Legacy camelCase key → display-label mapping for facilities
const FACILITY_KEY_MAP = {
  'securityCamera': 'Security Camera', 'luggageStorage': 'Luggage Storage',
  'banquet': 'Banquet', 'conferenceRoom': 'Conference Room', 'petsAllowed': 'Pets Allowed',
  'outdoorDining': 'Outdoor Dining Area', 'outsideFoodAllowed': 'Outside Food Allowed',
  'noSmokingInside': 'No Smoking Inside', 'unmarriedCouplesAllowed': 'Unmarried Couples Allowed',
  'onlyMalesGroupAllowed': 'Only Males Group Allowed', 'nonVegFoodAllowed': 'Non Veg Food Allowed',
  'restaurant': 'Restaurant', 'kitchen': 'Kitchen', 'wheelchairAccess': 'WheelChair Access'
};

// Normalize amenities/facilities: convert camelCase keys to display labels, strip falsy values
const normalizeToggleData = (raw, keyMap) => {
  if (!raw || typeof raw !== 'object') return {};
  const normalized = {};
  for (const [key, value] of Object.entries(raw)) {
    if (!value || value === false) continue;
    const displayKey = keyMap[key] || key;
    normalized[displayKey] = typeof value === 'object' ? value : { type: 'free' };
  }
  return normalized;
};

// Step definitions
const STEPS = [
  { id: 1, title: 'Property Details', icon: Building2 },
  { id: 2, title: 'Location Details', icon: MapPin },
  { id: 3, title: 'Room Inventory', icon: Home },
  { id: 4, title: 'Amenities & Policies', icon: Wifi },
  { id: 5, title: 'Banking Details', icon: CreditCard },
  { id: 6, title: 'Review & Submit', icon: ClipboardCheck },
];

// ==================== SUB COMPONENTS ====================

// Progress Tracker Bar
const ProgressTracker = ({ currentStep, completedSteps }) => {
  return (
    <div className="w-full mb-8">
      {/* Desktop Progress Bar */}
      <div className="hidden md:flex items-center justify-between">
        {STEPS.map((step, index) => {
          const isCompleted = completedSteps.includes(step.id);
          const isCurrent = currentStep === step.id;
          const Icon = step.icon;
          
          return (
            <React.Fragment key={step.id}>
              <div className="flex flex-col items-center">
                <div 
                  className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                    isCompleted 
                      ? 'bg-teal-600 text-white' 
                      : isCurrent 
                        ? 'bg-teal-100 text-teal-600 ring-2 ring-teal-600 ring-offset-2' 
                        : 'bg-gray-100 text-gray-400'
                  }`}
                >
                  {isCompleted ? <Check size={20} strokeWidth={3} /> : <Icon size={20} />}
                </div>
                <span className={`mt-2 text-xs font-medium text-center max-w-[80px] ${
                  isCurrent ? 'text-teal-600' : isCompleted ? 'text-gray-700' : 'text-gray-400'
                }`}>
                  {step.title}
                </span>
              </div>
              {index < STEPS.length - 1 && (
                <div className={`flex-1 h-1 mx-2 rounded ${
                  completedSteps.includes(STEPS[index + 1].id) || completedSteps.includes(step.id)
                    ? 'bg-teal-600' 
                    : 'bg-gray-200'
                }`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
      
      {/* Mobile Progress Bar */}
      <div className="md:hidden">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">
            Step {currentStep} of {STEPS.length}
          </span>
          <span className="text-sm text-teal-600 font-medium">
            {STEPS[currentStep - 1]?.title}
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-teal-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${(currentStep / STEPS.length) * 100}%` }}
          />
        </div>
      </div>
    </div>
  );
};

// Input Field
const InputField = ({ label, required, error, ...props }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1.5">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <input
      {...props}
      className={`w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-teal-500 outline-none transition-all bg-white ${
        error ? 'border-red-300' : 'border-gray-300'
      }`}
    />
    {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
  </div>
);

// Select Field
const SelectField = ({ label, required, options, error, ...props }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1.5">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <select
      {...props}
      className={`w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-teal-500 outline-none transition-all bg-white ${
        error ? 'border-red-300' : 'border-gray-300'
      }`}
    >
      <option value="">Select...</option>
      {options.map(opt => (
        <option key={opt} value={opt}>{opt}</option>
      ))}
    </select>
    {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
  </div>
);

// Searchable single-select dropdown (Step 2 — State). Type to filter the
// options; click to select. UI-only — value semantics unchanged.
const SearchableDropdown = ({ label, required, value, onChange, options, placeholder, disabled, error, testId }) => {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const wrapRef = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setIsOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);
  const filtered = (options || []).filter((o) => o.toLowerCase().includes((query || '').toLowerCase()));
  return (
    <div ref={wrapRef}>
      <label className="block text-sm font-medium text-gray-700 mb-1.5">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="relative">
        <input
          type="text"
          disabled={disabled}
          value={isOpen ? query : (value || '')}
          onChange={(e) => { setQuery(e.target.value); setIsOpen(true); }}
          onFocus={() => { if (!disabled) { setQuery(''); setIsOpen(true); } }}
          placeholder={placeholder}
          className={`w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-teal-500 outline-none bg-white disabled:bg-gray-50 disabled:cursor-not-allowed ${error ? 'border-red-300' : 'border-gray-300'}`}
          data-testid={testId}
        />
        <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        {isOpen && !disabled && (
          <div className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-auto">
            {filtered.length === 0 && <div className="px-3 py-2 text-sm text-gray-400">No matches</div>}
            {filtered.map((o) => (
              <button
                key={o}
                type="button"
                onClick={() => { onChange(o); setIsOpen(false); setQuery(''); }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-teal-50 ${value === o ? 'bg-teal-50 text-teal-700 font-medium' : 'text-gray-700'}`}
              >
                {o}
              </button>
            ))}
          </div>
        )}
      </div>
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </div>
  );
};
const StarRating = ({ value, onChange }) => (
  <div className="flex items-center gap-1">
    {[1, 2, 3, 4, 5].map(star => (
      <button
        key={star}
        type="button"
        onClick={() => onChange?.(star)}
        className="p-1 transition-all cursor-pointer hover:scale-110"
      >
        <Star
          size={28}
          className={`${star <= value ? 'fill-yellow-400 text-yellow-400' : 'text-gray-200'} transition-colors`}
        />
      </button>
    ))}
  </div>
);

// Amenity Checkbox Card
const AmenityCard = ({ label, isSelected, onToggle, config, onConfigChange }) => {
  return (
    <div
      className={`px-4 py-3 rounded-xl border-2 transition-all cursor-pointer ${
        isSelected 
          ? 'border-teal-500 bg-teal-50/30' 
          : 'border-gray-100 hover:border-gray-200'
      }`}
      onClick={onToggle}
    >
      <div className="flex items-center gap-3">
        <div className={`w-5 h-5 rounded flex items-center justify-center ${
          isSelected ? 'bg-teal-500 text-white' : 'bg-gray-100'
        }`}>
          {isSelected && <Check size={14} strokeWidth={3} />}
        </div>
        <span className={`text-sm font-medium ${isSelected ? 'text-gray-900' : 'text-gray-600'}`}>
          {label}
        </span>
      </div>
      
      {isSelected && (
        <div className="mt-3 flex gap-2" onClick={e => e.stopPropagation()}>
          <button
            type="button"
            onClick={() => onConfigChange?.({ type: 'free' })}
            className={`flex-1 py-1.5 text-xs font-bold rounded-lg ${
              config?.type === 'free' ? 'bg-teal-500 text-white' : 'bg-gray-100 text-gray-600'
            }`}
          >
            FREE
          </button>
          <button
            type="button"
            onClick={() => onConfigChange?.({ type: 'paid', price: config?.price || 0 })}
            className={`flex-1 py-1.5 text-xs font-bold rounded-lg ${
              config?.type === 'paid' ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-600'
            }`}
          >
            PAID
          </button>
        </div>
      )}
      
      {isSelected && config?.type === 'paid' && (
        <div className="mt-2" onClick={e => e.stopPropagation()}>
          <input
            type="number"
            placeholder="Price ₹"
            value={config?.price || ''}
            onChange={(e) => onConfigChange?.({ type: 'paid', price: parseFloat(e.target.value) || 0 })}
            className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg"
          />
        </div>
      )}
    </div>
  );
};

// Review Field Display
const ReviewField = ({ label, value }) => (
  // min-w-0 lets the grid cell shrink; break-words + overflow-wrap:anywhere
  // wrap long unbroken strings (e.g. emails) inside the card on mobile.
  <div className="py-2 min-w-0">
    <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">{label}</span>
    <p className="text-sm text-gray-900 mt-0.5 break-words [overflow-wrap:anywhere]">{value || <span className="text-gray-400">Not provided</span>}</p>
  </div>
);

// ==================== MAIN COMPONENT ====================

// Mega-Spec Item 4 (Step 2) — City dropdown driven by the selected State.
// Loads cities from the shared city master (getCitiesForState) + persisted
// custom cities (when authenticated). "Others" reveals a manual-entry field;
// such cities follow the existing custom-city persistence on submit.
const CityDropdown = ({ state, value, onChange, error }) => {
  const [customCities, setCustomCities] = useState([]);
  const baseCities = getCitiesForState(state);

  useEffect(() => {
    if (!state) { setCustomCities([]); return; }
    const token = localStorage.getItem('token') || localStorage.getItem('access_token');
    if (!token) { setCustomCities([]); return; }  // public form → static list only
    const ctrl = new AbortController();
    fetch(`${API_BASE}/api/settings/custom-cities?state=${encodeURIComponent(state)}`,
      { headers: { Authorization: `Bearer ${token}` }, signal: ctrl.signal })
      .then(r => (r.ok ? r.json() : []))
      .then(rows => {
        if (!Array.isArray(rows)) { setCustomCities([]); return; }
        setCustomCities(rows.map(r => (r && typeof r.name === 'string' ? r.name.trim() : '')).filter(Boolean));
      })
      .catch(() => setCustomCities([]));
    return () => ctrl.abort();
  }, [state]);

  const cities = (() => {
    const seen = new Set(baseCities.map(c => c.toLowerCase()));
    const merged = [...baseCities];
    for (const c of customCities) {
      if (!seen.has(c.toLowerCase())) { merged.push(c); seen.add(c.toLowerCase()); }
    }
    return merged;
  })();

  const isPresetMatch = !!value && cities.includes(value);
  const [otherMode, setOtherMode] = useState(!!value && !isPresetMatch && !!state);

  useEffect(() => {
    const matches = state && getCitiesForState(state).includes(value);
    setOtherMode(!!value && !matches && !!state);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const wrapRef = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setIsOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);
  const filteredCities = cities.filter((c) => c.toLowerCase().includes((query || '').toLowerCase()));

  if (otherMode) {
    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">City <span className="text-red-500">*</span></label>
        <div className="flex gap-2">
          <input
            type="text"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Type your city"
            className={`flex-1 px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-teal-500 outline-none bg-white ${error ? 'border-red-300' : 'border-gray-300'}`}
            data-testid="master-city-other-input"
          />
          <button
            type="button"
            onClick={() => { setOtherMode(false); onChange(''); }}
            className="px-3 py-2 text-xs text-gray-500 hover:text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 whitespace-nowrap"
            data-testid="master-city-back-btn"
          >
            Back to list
          </button>
        </div>
        {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
      </div>
    );
  }

  return (
    <div ref={wrapRef}>
      <label className="block text-sm font-medium text-gray-700 mb-1.5">City <span className="text-red-500">*</span></label>
      <div className="relative">
        <input
          type="text"
          disabled={!state}
          value={isOpen ? query : (value || '')}
          onChange={(e) => { setQuery(e.target.value); setIsOpen(true); }}
          onFocus={() => { if (state) { setQuery(''); setIsOpen(true); } }}
          placeholder={state ? 'Type to search city…' : 'Select a state first'}
          className={`w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-teal-500 outline-none bg-white disabled:bg-gray-50 disabled:cursor-not-allowed ${error ? 'border-red-300' : 'border-gray-300'}`}
          data-testid="master-city-select"
        />
        <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        {isOpen && state && (
          <div className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-auto">
            {filteredCities.length === 0 && (
              <div className="px-3 py-2 text-sm text-gray-400">No matching cities</div>
            )}
            {filteredCities.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => { onChange(c); setIsOpen(false); setQuery(''); }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-teal-50 ${value === c ? 'bg-teal-50 text-teal-700 font-medium' : 'text-gray-700'}`}
                data-testid={`master-city-option-${c}`}
              >
                {c}
              </button>
            ))}
            <button
              type="button"
              onClick={() => { setOtherMode(true); onChange(''); setIsOpen(false); setQuery(''); }}
              className="w-full text-left px-3 py-2 text-sm border-t border-gray-100 hover:bg-amber-50 text-amber-700 font-medium"
              data-testid="master-city-option-others"
            >
              Others (enter manually)
            </button>
          </div>
        )}
      </div>
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </div>
  );
};

const CANCELLATION_POLICIES = [
  { value: 'free', label: 'Free Cancellation' },
  { value: '24h', label: '24 Hours Before Check-in' },
  { value: '48h', label: '48 Hours Before Check-in' },
  { value: '72h', label: '72 Hours Before Check-in' },
  { value: 'non_refundable', label: 'Non-Refundable' },
  { value: 'custom', label: 'Custom Policy' },
];

const getEmptyFormData = () => ({
  propertyName: '',
  propertyType: 'Hotel',
  starRating: 3,
  totalRooms: '',
  address: '',
  state: '',
  city: '',
  pinCode: '',
  googleMapLink: '',
  description: '',
  ownerName: '',
  mobile: '',
  email: '',
  altContact: '',
  rooms: [],
  amenities: {},
  facilities: {},
  other_facilities: '',
  banking: {
    accountHolderName: '',
    bankName: '',
    accountNumber: '',
    ifscCode: '',
    branchName: '',
    upiId: '',
    cancelledChequeUrl: '',
    cancelledChequeName: '',
    qrCodeUrl: '',
    qrCodeName: ''
  },
  propertyStatus: 'Active',
  onboardingStatus: 'In Progress'
});

// Auto-detect occupancy based on room type name
const detectOccupancy = (roomName) => {
  const name = (roomName || '').toLowerCase();
  if (name.includes('single')) return 1;
  if (name.includes('double')) return 2;
  if (name.includes('triple')) return 3;
  if (name.includes('family') || name.includes('quad')) return 4;
  if (name.includes('suite')) return 2;
  if (name.includes('deluxe')) return 2;
  if (name.includes('standard')) return 2;
  return 2;
};

const MultiStepOnboardingForm = ({
  initialData = null,
  onSave,
  saving = false,
  isPublic = false,
  onFormDataChange = null
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [formData, setFormData] = useState(getEmptyFormData());
  const [errors, setErrors] = useState({});
  const [bankUploading, setBankUploading] = useState({ cheque: false, qr: false });

  // Mega-Spec Item 4 (Step 5) — upload optional cheque/QR to the public
  // master-form staging endpoint and store the returned URL in banking.
  const handleBankDocUpload = async (docType, file) => {
    if (!file) return;
    const isQr = docType === 'qr';
    const allowed = isQr ? ['.jpg', '.jpeg', '.png', '.webp'] : ['.pdf', '.jpg', '.jpeg', '.png', '.webp'];
    const lower = file.name.toLowerCase();
    if (!allowed.some(ext => lower.endsWith(ext))) {
      alert(`Unsupported file type. Allowed: ${allowed.join(', ')}`);
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      alert('File size exceeds 10MB limit');
      return;
    }
    try {
      setBankUploading(prev => ({ ...prev, [docType]: true }));
      const fd = new FormData();
      fd.append('file', file);
      const token = localStorage.getItem('token') || localStorage.getItem('access_token');
      const res = await fetch(`${API_BASE}/api/master-form/upload?doc_type=${docType}`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `Upload failed (HTTP ${res.status})`);
      }
      const data = await res.json();
      if (isQr) {
        handleBankingChange('qrCodeUrl', data.url);
        handleBankingChange('qrCodeName', data.name);
      } else {
        handleBankingChange('cancelledChequeUrl', data.url);
        handleBankingChange('cancelledChequeName', data.name);
      }
    } catch (e) {
      alert(e.message || 'Upload failed');
    } finally {
      setBankUploading(prev => ({ ...prev, [docType]: false }));
    }
  };

  useEffect(() => {
    if (initialData) {
      setFormData({
        ...getEmptyFormData(),
        ...initialData,
        amenities: normalizeToggleData(initialData.amenities, AMENITY_KEY_MAP),
        facilities: normalizeToggleData(initialData.facilities, FACILITY_KEY_MAP),
        banking: {
          ...getEmptyFormData().banking,
          ...(initialData.banking || {})
        }
      });
    }
  }, [initialData]);

  // Notify parent of every formData change so the public onboarding page
  // can debounce and PUT draft state to the backend. Skip the very first
  // mount so we don't resave an empty-shell draft before the owner types.
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!onFormDataChange) return;
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    onFormDataChange(formData);
  }, [formData, onFormDataChange]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when field is updated
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleBankingChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      banking: { ...prev.banking, [field]: value }
    }));
  };

  // Room Management
  const addRoom = () => {
    const defaultCategory = 'Standard Room';
    setFormData(prev => ({
      ...prev,
      rooms: [...prev.rooms, { 
        id: crypto.randomUUID(), 
        category: defaultCategory, 
        roomTypeCategory: 'Non-AC',
        count: '', 
        roomSize: '',
        roomSizeUnit: 'sq m',
        basePrice: '', 
        weekendPrice: '',
        extraAdultPrice: '',
        noOfAdults: '',
        noOfChildren: '',
        amenities: {},
        customRoomType: '',
        occupancy: ''
      }]
    }));
  };

  const updateRoom = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      rooms: prev.rooms.map(r => {
        if (r.id !== id) return r;
        const updated = { ...r, [field]: value };
        return updated;
      })
    }));
  };

  const removeRoom = (id) => {
    setFormData(prev => ({
      ...prev,
      rooms: prev.rooms.filter(r => r.id !== id)
    }));
  };

  // Amenity Management
  const toggleAmenity = (type, key) => {
    setFormData(prev => {
      const current = { ...prev[type] };
      if (current[key]) {
        delete current[key];
      } else {
        current[key] = { type: 'free' };
      }
      return { ...prev, [type]: current };
    });
  };

  const updateAmenityConfig = (type, key, config) => {
    setFormData(prev => ({
      ...prev,
      [type]: { ...prev[type], [key]: config }
    }));
  };

  // Mega-Spec Item 4 (Step 4) — per-room (room-type specific) amenities.
  const toggleRoomAmenity = (roomId, amenity) => {
    setFormData(prev => ({
      ...prev,
      rooms: prev.rooms.map(r => {
        if (r.id !== roomId) return r;
        const current = { ...(r.amenities || {}) };
        if (current[amenity]) {
          delete current[amenity];
        } else {
          // Bathroom requires a Private/Shared choice (default blank → must pick).
          current[amenity] = amenity === 'Bathroom' ? { variant: '' } : true;
        }
        return { ...r, amenities: current };
      })
    }));
  };

  const setRoomBathroomVariant = (roomId, variant) => {
    setFormData(prev => ({
      ...prev,
      rooms: prev.rooms.map(r => {
        if (r.id !== roomId) return r;
        return { ...r, amenities: { ...(r.amenities || {}), Bathroom: { variant } } };
      })
    }));
  };

  // Mega-Spec Item 4 (Step 4) — Property facilities (Pet Allowed / Pool /
  // Couple Friendly). Stored under formData.facilities with structured values.
  const toggleFacility = (key, defaultVal) => {
    setFormData(prev => {
      const current = { ...(prev.facilities || {}) };
      if (current[key]) {
        delete current[key];
      } else {
        current[key] = defaultVal;
      }
      return { ...prev, facilities: current };
    });
  };

  const setFacilityValue = (key, val) => {
    setFormData(prev => ({
      ...prev,
      facilities: { ...(prev.facilities || {}), [key]: { ...(prev.facilities?.[key] || {}), ...val } }
    }));
  };

  // Mega-Spec: Yes/No facility selection (mandatory).
  const setYesNoFacility = (key, value) => {
    setFormData(prev => ({
      ...prev,
      facilities: { ...(prev.facilities || {}), [key]: { ...(prev.facilities?.[key] || {}), value } }
    }));
    if (errors.facilities) setErrors(prev => ({ ...prev, facilities: null }));
  };

  // Validation for each step
  const validateStep = (step) => {
    const newErrors = {};
    
    switch (step) {
      case 1: // Property Details
        if (!formData.propertyName?.trim()) newErrors.propertyName = 'Property name is required';
        if (!formData.propertyType) newErrors.propertyType = 'Property type is required';
        {
          const tr = parseInt(formData.totalRooms);
          if (!formData.totalRooms || tr < 1) newErrors.totalRooms = 'Total rooms is required (min 1)';
          else if (tr > 1000) newErrors.totalRooms = 'Total rooms cannot exceed 1000';
        }
        if (!formData.ownerName?.trim()) newErrors.ownerName = 'Owner name is required';
        if (!formData.email?.trim()) newErrors.email = 'Email is required';
        if (!formData.mobile?.trim()) newErrors.mobile = 'Mobile number is required';
        break;
      case 2: // Location Details
        if (!formData.address?.trim()) newErrors.address = 'Address is required';
        if (!formData.state) newErrors.state = 'State is required';
        if (!formData.city?.trim()) newErrors.city = 'City is required';
        if (!formData.pinCode?.trim()) newErrors.pinCode = 'Pin code is required';
        // 3A — Google Map Link is mandatory and must be a valid map URL.
        {
          const gm = (formData.googleMapLink || '').trim();
          if (!gm) {
            newErrors.googleMapLink = 'Google Map Link is required';
          } else if (!/^https?:\/\/(www\.)?(google\.[a-z.]+\/maps|maps\.google\.[a-z.]+|maps\.app\.goo\.gl|goo\.gl\/maps|g\.co\/kgs)/i.test(gm)) {
            newErrors.googleMapLink = 'Please enter a valid Google Maps link (e.g. https://maps.google.com/… or https://maps.app.goo.gl/…)';
          }
        }
        break;
      case 3: // Room Inventory — at least one room + all fields mandatory
        {
          if (!formData.rooms || formData.rooms.length === 0) {
            newErrors.roomInventory = 'Please add at least one room category before proceeding.';
            break;
          }
          const num = (v) => { const n = parseFloat(v); return Number.isNaN(n) ? null : n; };
          for (let i = 0; i < formData.rooms.length; i++) {
            const r = formData.rooms[i];
            const label = r.category === 'Other' ? (r.customRoomType || `Room ${i + 1}`) : r.category;
            if (r.category === 'Other' && !r.customRoomType?.trim()) {
              newErrors.roomInventory = `Room ${i + 1}: please enter the custom room type.`; break;
            }
            if (num(r.count) === null || num(r.count) < 1) { newErrors.roomInventory = `${label}: Number of Rooms is required.`; break; }
            if (num(r.roomSize) === null || num(r.roomSize) <= 0) { newErrors.roomInventory = `${label}: Room Size is required.`; break; }
            if (!r.roomSizeUnit) { newErrors.roomInventory = `${label}: Room Size unit is required.`; break; }
            if (num(r.noOfAdults) === null || num(r.noOfAdults) < 1) { newErrors.roomInventory = `${label}: No of Adults is required.`; break; }
            if (r.noOfChildren === '' || r.noOfChildren === undefined || num(r.noOfChildren) === null || num(r.noOfChildren) < 0) { newErrors.roomInventory = `${label}: No of Children is required.`; break; }
            if (num(r.basePrice) === null || num(r.basePrice) <= 0) { newErrors.roomInventory = `${label}: Base Price is required.`; break; }
            if (num(r.weekendPrice) === null || num(r.weekendPrice) <= 0) { newErrors.roomInventory = `${label}: Weekend Price is required.`; break; }
            if (num(r.extraAdultPrice) === null || num(r.extraAdultPrice) < 0) { newErrors.roomInventory = `${label}: Extra Adult Price is required.`; break; }
          }
          if (!newErrors.roomInventory) {
            const totalRoomsNum = parseInt(formData.totalRooms) || 0;
            const inventorySum = formData.rooms.reduce((sum, r) => sum + (parseInt(r.count) || 0), 0);
            if (totalRoomsNum > 0 && inventorySum > totalRoomsNum) {
              newErrors.roomInventory = `Room inventory (${inventorySum}) exceeds total rooms (${totalRoomsNum}). Please adjust.`;
            }
          }
        }
        break;
      case 4: // Amenities & Policies
        {
          // Bathroom mandatory for EVERY room + Private/Shared chosen.
          const bathBad = (formData.rooms || []).some(r => {
            const b = r.amenities?.Bathroom;
            if (!b) return true;  // bathroom not selected
            return (typeof b === 'object') && !b.variant;  // selected but no variant
          });
          if ((formData.rooms || []).length === 0) {
            newErrors.roomAmenities = 'Please add room categories in Step 3 first.';
          } else if (bathBad) {
            newErrors.roomAmenities = 'Bathroom (Private/Shared) is mandatory for every room category.';
          }
          // All Yes/No facilities must be answered.
          const f = formData.facilities || {};
          const missingYN = YES_NO_FACILITIES.filter(name => !f[name]?.value);
          if (missingYN.length > 0) {
            newErrors.facilities = `Please select Yes/No for: ${missingYN.join(', ')}.`;
          }
          // Pet Allowed = Yes → charge required; Paid → amount required.
          const pet = f['Pet Allowed'];
          if (pet?.value === 'Yes') {
            if (!pet.charge) newErrors.facilities = 'Please choose Free or Paid for Pet Allowed.';
            else if (pet.charge === 'paid' && !(parseFloat(pet.amount) > 0)) newErrors.facilities = 'Please enter the Pet Allowed charge amount.';
          }
          // Free/Paid facilities (when enabled) → charge + paid-amount; Pool → pool type.
          for (const name of CHARGE_FACILITIES) {
            const v = f[name];
            if (!v) continue;  // optional, not enabled
            if (!v.charge) { newErrors.facilities = `Please choose Free or Paid for ${name}.`; break; }
            if (v.charge === 'paid' && !(parseFloat(v.amount) > 0)) { newErrors.facilities = `Please enter the ${name} charge amount.`; break; }
            if (name === 'Swimming Pool' && !v.poolType) { newErrors.facilities = 'Please select Indoor or Outdoor for Swimming Pool.'; break; }
          }
        }
        break;
      case 5: // Banking — QR mandatory + (full bank details OR cancelled cheque)
        {
          const b = formData.banking || {};
          // 3B — Payment QR Code is mandatory.
          if (!b.qrCodeUrl) {
            newErrors.qrCode = 'Payment QR Code is required. Please upload a QR code.';
          }
          // 3B — must satisfy Option A (all bank fields) OR Option B (cheque).
          const bankComplete = Boolean(
            (b.accountHolderName || '').trim() &&
            (b.bankName || '').trim() &&
            (b.accountNumber || '').trim() &&
            (b.ifscCode || '').trim() &&
            (b.branchName || '').trim()
          );
          const hasCheque = Boolean(b.cancelledChequeUrl);
          if (!bankComplete && !hasCheque) {
            newErrors.bankDetails = 'Provide complete bank details (Account Holder, Bank, Account No., IFSC, Branch) OR upload a Cancelled Cheque.';
          }
        }
        break;
      case 6: // Review - No validation needed
        break;
      default:
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (!completedSteps.includes(currentStep)) {
        setCompletedSteps(prev => [...prev, currentStep]);
      }
      setCurrentStep(prev => Math.min(prev + 1, STEPS.length));
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleFinalSubmit = () => {
    // Only ever called by the explicit "Submit" button which renders ONLY
    // on Step 6 (Review). The <form> itself never submits implicitly, so a
    // phantom/implicit submit can never create the property early.
    if (currentStep < STEPS.length) {
      handleNext();
      return;
    }
    if (validateStep(currentStep)) {
      onSave?.(formData);
    }
  };

  // ==================== STEP RENDERERS ====================

  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <InputField
          label="Property Name"
          required
          value={formData.propertyName}
          onChange={(e) => handleChange('propertyName', e.target.value)}
          placeholder="e.g. Grand Horizon Hotel"
          error={errors.propertyName}
        />
        <SelectField
          label="Property Type"
          required
          value={formData.propertyType}
          onChange={(e) => handleChange('propertyType', e.target.value)}
          options={PROPERTY_TYPES}
          error={errors.propertyType}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Star Rating</label>
        <StarRating
          value={formData.starRating}
          onChange={(val) => handleChange('starRating', val)}
        />
      </div>

      {/* Total Number of Rooms (Mandatory) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Total Number of Rooms <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            value={formData.totalRooms}
            onChange={(e) => handleChange('totalRooms', e.target.value)}
            placeholder="e.g. 50"
            min="1"
            max="1000"
            className={`w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-teal-500 outline-none ${
              errors.totalRooms ? 'border-red-300 bg-red-50' : 'border-gray-300'
            }`}
            data-testid="total-rooms-input"
          />
          {errors.totalRooms && (
            <p className="text-red-500 text-xs mt-1">{errors.totalRooms}</p>
          )}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Property Description</label>
        <textarea
          value={formData.description}
          onChange={(e) => handleChange('description', e.target.value)}
          placeholder="Describe your property..."
          rows={3}
          className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none resize-none"
        />
      </div>

      <div className="border-t border-gray-200 pt-6">
        <h4 className="text-lg font-semibold text-gray-800 mb-4">Contact Information</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <InputField
            label="Owner / Manager Name"
            required
            value={formData.ownerName}
            onChange={(e) => handleChange('ownerName', e.target.value)}
            placeholder="Full Name"
            error={errors.ownerName}
          />
          <InputField
            label="Official Email"
            required
            type="email"
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)}
            placeholder="admin@hotel.com"
            error={errors.email}
          />
          <InputField
            label="Mobile Number"
            required
            type="tel"
            value={formData.mobile}
            onChange={(e) => handleChange('mobile', e.target.value)}
            placeholder="+91 9876543210"
            error={errors.mobile}
          />
          <InputField
            label="Alternative Contact"
            type="tel"
            value={formData.altContact}
            onChange={(e) => handleChange('altContact', e.target.value)}
            placeholder="Landline or Secondary Mobile"
          />
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-5">
      <InputField
        label="Full Address"
        required
        value={formData.address}
        onChange={(e) => handleChange('address', e.target.value)}
        placeholder="Street, Area, Landmark"
        error={errors.address}
      />
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <SearchableDropdown
          label="State"
          required
          value={formData.state}
          onChange={(v) => { handleChange('state', v); handleChange('city', ''); }}
          options={INDIAN_STATES}
          placeholder="Type to search state…"
          error={errors.state}
          testId="master-state-search"
        />
        <CityDropdown
          state={formData.state}
          value={formData.city}
          onChange={(val) => handleChange('city', val)}
          error={errors.city}
        />
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <InputField
          label="Pin Code"
          required
          value={formData.pinCode}
          onChange={(e) => handleChange('pinCode', e.target.value)}
          placeholder="6-digit PIN"
          maxLength={6}
          error={errors.pinCode}
        />
        <InputField
          label="Google Map Link"
          required
          value={formData.googleMapLink}
          onChange={(e) => handleChange('googleMapLink', e.target.value)}
          placeholder="https://maps.google.com/..."
          type="url"
          error={errors.googleMapLink}
        />
      </div>
    </div>
  );

  const renderStep3 = () => {
    const totalRoomsNum = parseInt(formData.totalRooms) || 0;
    const inventorySum = formData.rooms.reduce((sum, r) => sum + (parseInt(r.count, 10) || 0), 0);
    const isOverLimit = totalRoomsNum > 0 && inventorySum > totalRoomsNum;

    return (
      <div className="space-y-5">
        <div className="flex justify-between items-center">
          <p className="text-sm text-gray-500">Add room categories and inventory details</p>
          <button
            type="button"
            onClick={addRoom}
            className="flex items-center px-4 py-2 text-teal-600 border border-teal-200 rounded-lg hover:bg-teal-50 transition-colors font-medium text-sm"
            data-testid="add-room-btn"
          >
            <Plus size={18} className="mr-2" /> Add Room Category
          </button>
        </div>

        {/* Total Rooms Validation Banner */}
        {totalRoomsNum > 0 && formData.rooms.length > 0 && (
          <div className={`flex items-center justify-between px-4 py-3 rounded-xl border text-sm font-medium ${
            isOverLimit
              ? 'bg-red-50 border-red-200 text-red-700'
              : 'bg-teal-50 border-teal-200 text-teal-700'
          }`} data-testid="room-validation-banner">
            <span>Room Inventory: <strong>{inventorySum}</strong> / {totalRoomsNum} total rooms</span>
            {isOverLimit && <span className="text-red-600 text-xs">Exceeds total rooms limit!</span>}
          </div>
        )}
        {errors.roomInventory && (
          <p className="text-red-500 text-sm font-medium" data-testid="room-inventory-error">{errors.roomInventory}</p>
        )}

        {formData.rooms.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
            <Home size={48} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 mb-4">No rooms configured yet</p>
            <button
              type="button"
              onClick={addRoom}
              className="text-teal-600 hover:text-teal-700 font-medium text-sm"
            >
              Add your first room category
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {formData.rooms.map((room, index) => {
              const roomLabel = room.category === 'Other' ? (room.customRoomType || 'Custom Room') : room.category;
              return (
                <div key={room.id} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow" data-testid={`room-card-${index}`}>
                  {/* Card Header */}
                  <div className="px-5 py-3 bg-gradient-to-r from-slate-800 to-slate-700 flex items-center justify-between">
                    <h4 className="text-white font-semibold text-sm">{roomLabel}</h4>
                    <button
                      type="button"
                      onClick={() => removeRoom(room.id)}
                      className="p-1 text-slate-300 hover:text-red-300 rounded transition-colors"
                      data-testid={`remove-room-${index}`}
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>

                  <div className="p-5 space-y-4">
                    {/* Room Type Select */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Room Type</label>
                      <select
                        value={room.category}
                        onChange={(e) => {
                          updateRoom(room.id, 'category', e.target.value);
                          if (e.target.value !== 'Other') updateRoom(room.id, 'customRoomType', '');
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none bg-white"
                        data-testid={`room-category-${index}`}
                      >
                        {ROOM_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>

                    {room.category === 'Other' && (
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Custom Room Name</label>
                        <input
                          type="text"
                          value={room.customRoomType || ''}
                          onChange={(e) => updateRoom(room.id, 'customRoomType', e.target.value)}
                          placeholder="e.g. Executive Room"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none"
                        />
                      </div>
                    )}

                    {/* AC / Non-AC Toggle */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">AC Status</label>
                      <div className="flex gap-2" data-testid={`room-ac-toggle-${index}`}>
                        {ROOM_TYPE_CATEGORIES.map(type => (
                          <button
                            key={type}
                            type="button"
                            onClick={() => updateRoom(room.id, 'roomTypeCategory', type)}
                            className={`px-4 py-1.5 rounded-full text-xs font-bold transition-all ${
                              room.roomTypeCategory === type
                                ? type === 'AC'
                                  ? 'bg-blue-600 text-white shadow-sm'
                                  : 'bg-orange-500 text-white shadow-sm'
                                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                            }`}
                            data-testid={`room-ac-${type.toLowerCase().replace('-', '')}-${index}`}
                          >
                            {type}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Count & Room Size */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Number of Rooms <span className="text-red-500">*</span></label>
                        <input
                          type="number"
                          min={1}
                          value={room.count ?? ''}
                          onChange={(e) => updateRoom(room.id, 'count', e.target.value)}
                          placeholder="e.g. 5"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none"
                          data-testid={`room-count-${index}`}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Room Size <span className="text-red-500">*</span></label>
                        <div className="flex gap-2">
                          <input
                            type="number"
                            min={1}
                            value={room.roomSize ?? ''}
                            onChange={(e) => updateRoom(room.id, 'roomSize', e.target.value)}
                            placeholder="e.g. 150"
                            className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none"
                            data-testid={`room-size-${index}`}
                          />
                          <select
                            value={room.roomSizeUnit || 'sq m'}
                            onChange={(e) => updateRoom(room.id, 'roomSizeUnit', e.target.value)}
                            className="px-2 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none bg-white"
                            data-testid={`room-size-unit-${index}`}
                          >
                            <option value="sq ft">sq ft</option>
                            <option value="sq m">sq m</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Base Price */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Base Price (per night) <span className="text-red-500">*</span></label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">&#8377;</span>
                          <input
                            type="number"
                            min={0}
                            value={room.basePrice ?? ''}
                            onChange={(e) => updateRoom(room.id, 'basePrice', e.target.value)}
                            placeholder="Enter amount"
                            className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none"
                            data-testid={`room-base-price-${index}`}
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Weekend Price <span className="text-red-500">*</span></label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">&#8377;</span>
                          <input
                            type="number"
                            min={0}
                            value={room.weekendPrice ?? ''}
                            onChange={(e) => updateRoom(room.id, 'weekendPrice', e.target.value)}
                            placeholder="Enter amount"
                            className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none"
                            data-testid={`room-weekend-price-${index}`}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Extra Adult Price + Guests */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Extra Adult Price <span className="text-red-500">*</span></label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">&#8377;</span>
                          <input
                            type="number"
                            min={0}
                            value={room.extraAdultPrice ?? ''}
                            onChange={(e) => updateRoom(room.id, 'extraAdultPrice', e.target.value)}
                            placeholder="Enter amount"
                            className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none"
                            data-testid={`room-extra-adult-price-${index}`}
                          />
                        </div>
                      </div>
                    </div>

                    {/* No of Adults & Children */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">No of Adults <span className="text-red-500">*</span></label>
                        <input
                          type="number"
                          min={1}
                          max={20}
                          value={room.noOfAdults ?? ''}
                          onChange={(e) => updateRoom(room.id, 'noOfAdults', e.target.value)}
                          placeholder="e.g. 2"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none"
                          data-testid={`room-adults-${index}`}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">No of Children <span className="text-red-500">*</span></label>
                        <input
                          type="number"
                          min={0}
                          max={20}
                          value={room.noOfChildren ?? ''}
                          onChange={(e) => updateRoom(room.id, 'noOfChildren', e.target.value)}
                          placeholder="e.g. 1"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none"
                          data-testid={`room-children-${index}`}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Card Footer Summary */}
                  <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
                    <span>{room.count || 0} rooms &middot; {room.roomTypeCategory || 'AC'}</span>
                    <span className="font-semibold text-gray-700">{room.basePrice ? `₹${Number(room.basePrice).toLocaleString('en-IN')}/night` : ''}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Summary Bar */}
        {formData.rooms.length > 0 && (
          <div className={`p-4 rounded-xl flex justify-between items-center text-sm ${
            isOverLimit ? 'bg-red-700 text-white' : 'bg-gray-800 text-white'
          }`} data-testid="room-summary-bar">
            <span>Total Room Types: <strong>{formData.rooms.length}</strong></span>
            <span>Total Inventory: <strong>{inventorySum}</strong> rooms {totalRoomsNum > 0 ? `/ ${totalRoomsNum}` : ''}</span>
          </div>
        )}
      </div>
    );
  };

  const renderRoomAmenityChip = (room, amenity) => {
    const selected = !!room.amenities?.[amenity];
    const isBathroom = amenity === 'Bathroom';
    const bathVariant = isBathroom && room.amenities?.Bathroom?.variant;
    return (
      <div
        key={amenity}
        onClick={() => toggleRoomAmenity(room.id, amenity)}
        className={`px-3 py-2.5 rounded-lg border-2 cursor-pointer transition-all ${selected ? 'border-teal-500 bg-teal-50/40' : 'border-gray-100 hover:border-gray-200'}`}
        data-testid={`room-amenity-${room.id}-${amenity.toLowerCase()}`}
      >
        <div className="flex items-center gap-2">
          <div className={`w-4 h-4 rounded flex items-center justify-center ${selected ? 'bg-teal-500 text-white' : 'bg-gray-100'}`}>
            {selected && <Check size={11} strokeWidth={3} />}
          </div>
          <span className={`text-sm font-medium ${selected ? 'text-gray-900' : 'text-gray-600'}`}>{amenity}</span>
        </div>
        {isBathroom && selected && (
          <div className="mt-2 flex gap-1.5" onClick={(e) => e.stopPropagation()}>
            {BATHROOM_OPTIONS.map(opt => (
              <button
                key={opt}
                type="button"
                onClick={() => setRoomBathroomVariant(room.id, opt)}
                className={`flex-1 py-1 text-xs font-bold rounded-md ${bathVariant === opt ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-600'}`}
                data-testid={`room-bathroom-${room.id}-${opt.toLowerCase()}`}
              >
                {opt}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  // ---- Step 4 facility renderers ----
  const renderYesNoFacility = (name) => {
    const f = formData.facilities?.[name];
    const val = f?.value;
    const isPet = name === 'Pet Allowed';
    const pet = isPet ? f : null;
    return (
      <div key={name} className={`px-4 py-3 rounded-xl border-2 transition-all ${val ? 'border-teal-500 bg-teal-50/30' : 'border-gray-100'}`} data-testid={`facility-${name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm font-medium text-gray-700">{name} <span className="text-red-500">*</span></span>
          <div className="flex gap-1.5">
            {['Yes', 'No'].map(opt => (
              <button key={opt} type="button"
                onClick={() => setYesNoFacility(name, opt)}
                className={`px-3 py-1 text-xs font-bold rounded-lg ${val === opt ? (opt === 'Yes' ? 'bg-teal-600 text-white' : 'bg-gray-700 text-white') : 'bg-gray-100 text-gray-600'}`}
                data-testid={`facility-${name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${opt.toLowerCase()}`}
              >{opt}</button>
            ))}
          </div>
        </div>
        {isPet && pet?.value === 'Yes' && (
          <div className="mt-3 space-y-2">
            <div className="flex gap-2">
              {['free', 'paid'].map(t => (
                <button key={t} type="button"
                  onClick={() => setFacilityValue('Pet Allowed', { value: 'Yes', charge: t, ...(t === 'free' ? { amount: '' } : {}) })}
                  className={`flex-1 py-1.5 text-xs font-bold rounded-lg uppercase ${pet.charge === t ? (t === 'free' ? 'bg-teal-500 text-white' : 'bg-gray-800 text-white') : 'bg-gray-100 text-gray-600'}`}
                  data-testid={`facility-pet-${t}`}
                >{t}</button>
              ))}
            </div>
            {pet.charge === 'paid' && (
              <input type="number" min={0} placeholder="Charge ₹" value={pet.amount ?? ''}
                onChange={(e) => setFacilityValue('Pet Allowed', { value: 'Yes', charge: 'paid', amount: e.target.value })}
                className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg"
                data-testid="facility-pet-amount" />
            )}
          </div>
        )}
      </div>
    );
  };

  const renderToggleFacility = (name) => {
    const on = !!formData.facilities?.[name];
    const tid = `facility-${name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
    return (
      <div key={name}
        onClick={() => toggleFacility(name, { selected: true })}
        className={`px-4 py-3 rounded-xl border-2 cursor-pointer transition-all ${on ? 'border-teal-500 bg-teal-50/40' : 'border-gray-100 hover:border-gray-200'}`}
        data-testid={tid}
      >
        <div className="flex items-center gap-3">
          <div className={`w-5 h-5 rounded flex items-center justify-center ${on ? 'bg-teal-500 text-white' : 'bg-gray-100'}`}>
            {on && <Check size={14} strokeWidth={3} />}
          </div>
          <span className={`text-sm font-medium ${on ? 'text-gray-900' : 'text-gray-600'}`}>{name}</span>
        </div>
      </div>
    );
  };

  const renderChargeFacility = (name) => {
    const f = formData.facilities?.[name];
    const on = !!f;
    const isPool = name === 'Swimming Pool';
    const tid = `facility-${name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
    return (
      <div key={name}
        onClick={() => toggleFacility(name, isPool ? { charge: '', poolType: '' } : { charge: '' })}
        className={`px-4 py-3 rounded-xl border-2 cursor-pointer transition-all ${on ? 'border-teal-500 bg-teal-50/40' : 'border-gray-100 hover:border-gray-200'}`}
        data-testid={tid}
      >
        <div className="flex items-center gap-3">
          <div className={`w-5 h-5 rounded flex items-center justify-center ${on ? 'bg-teal-500 text-white' : 'bg-gray-100'}`}>
            {on && <Check size={14} strokeWidth={3} />}
          </div>
          <span className={`text-sm font-medium ${on ? 'text-gray-900' : 'text-gray-600'}`}>{name}</span>
        </div>
        {on && (
          <div className="mt-3 space-y-2" onClick={(e) => e.stopPropagation()}>
            <div className="flex gap-2">
              {['free', 'paid'].map(t => (
                <button key={t} type="button"
                  onClick={() => setFacilityValue(name, { charge: t, ...(t === 'free' ? { amount: '' } : {}) })}
                  className={`flex-1 py-1.5 text-xs font-bold rounded-lg uppercase ${f.charge === t ? (t === 'free' ? 'bg-teal-500 text-white' : 'bg-gray-800 text-white') : 'bg-gray-100 text-gray-600'}`}
                  data-testid={`${tid}-${t}`}
                >{t}</button>
              ))}
            </div>
            {f.charge === 'paid' && (
              <input type="number" min={0} placeholder="Charge ₹" value={f.amount ?? ''}
                onChange={(e) => setFacilityValue(name, { amount: e.target.value })}
                className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg"
                data-testid={`${tid}-amount`} />
            )}
            {isPool && (
              <div className="flex gap-2 pt-1">
                {['Indoor', 'Outdoor'].map(v => (
                  <button key={v} type="button"
                    onClick={() => setFacilityValue('Swimming Pool', { poolType: v })}
                    className={`flex-1 py-1.5 text-xs font-bold rounded-lg ${f.poolType === v ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-600'}`}
                    data-testid={`facility-pool-${v.toLowerCase()}`}
                  >{v}</button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderStep4 = () => {
    return (
      <div className="space-y-8">
        {/* Room-type-specific amenities */}
        <div>
          <h4 className="text-lg font-semibold text-gray-800 mb-1">Room Amenities</h4>
          <p className="text-sm text-gray-500 mb-4">Select amenities for each room category. Bathroom (Private/Shared) is mandatory for every room.</p>
          {errors.roomAmenities && (
            <p className="text-red-500 text-sm font-medium mb-3" data-testid="room-amenities-error">{errors.roomAmenities}</p>
          )}
          {(formData.rooms || []).length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
              <Home size={36} className="mx-auto text-gray-300 mb-2" />
              <p className="text-gray-500 text-sm">Add room categories in Step 3 to configure amenities.</p>
            </div>
          ) : (
            <div className="space-y-5">
              {formData.rooms.map((room, idx) => {
                const roomLabel = room.category === 'Other' ? (room.customRoomType || 'Custom Room') : room.category;
                return (
                  <div key={room.id} className="border border-gray-200 rounded-xl overflow-hidden" data-testid={`room-amenities-card-${idx}`}>
                    <div className="px-4 py-2.5 bg-gradient-to-r from-slate-800 to-slate-700">
                      <span className="text-white font-semibold text-sm">{roomLabel} <span className="text-slate-300 font-normal">· {room.roomTypeCategory || 'AC'}</span></span>
                    </div>
                    <div className="p-4 grid grid-cols-2 md:grid-cols-5 gap-2.5">
                      {ROOM_AMENITIES.map(amenity => renderRoomAmenityChip(room, amenity))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Property Facilities & Policies */}
        <div>
          <h4 className="text-lg font-semibold text-gray-800 mb-1">Property Facilities & Policies</h4>
          {errors.facilities && (
            <p className="text-red-500 text-sm font-medium mb-3" data-testid="facilities-error">{errors.facilities}</p>
          )}
          {/* Yes/No (mandatory) */}
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mt-3 mb-2">Policies (Yes / No required)</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {YES_NO_FACILITIES.map(name => renderYesNoFacility(name))}
          </div>
          {/* Free/Paid */}
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mt-5 mb-2">Chargeable Facilities</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {CHARGE_FACILITIES.map(name => renderChargeFacility(name))}
          </div>
          {/* Simple toggles */}
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mt-5 mb-2">Other Facilities</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {TOGGLE_FACILITIES.map(name => renderToggleFacility(name))}
          </div>

          {/* Other Amenities / Facilities — free text */}
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Other Amenities / Facilities</label>
            <textarea
              rows={3}
              value={formData.other_facilities || ''}
              onChange={(e) => handleChange('other_facilities', e.target.value)}
              placeholder="Enter any amenities/facilities not listed above (e.g. Lift, EV Charging, Garden, Terrace, Rooftop Cafe)"
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none resize-y"
              data-testid="other-facilities-input"
            />
          </div>
        </div>
      </div>
    );
  };

  const renderStep5 = () => {
    const b = formData.banking || {};
    return (
      <div className="space-y-5">
        <div className="bg-emerald-50 border border-emerald-200 p-4 rounded-xl">
          <p className="text-sm text-emerald-700">
            <strong>Payment QR Code is required.</strong> You must also either fill the complete bank
            details (Account Holder, Bank, Account No., IFSC, Branch) <strong>or</strong> upload a Cancelled Cheque.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <InputField
            label="Account Holder Name"
            value={b.accountHolderName}
            onChange={(e) => handleBankingChange('accountHolderName', e.target.value)}
            placeholder="As per bank records"
          />
          <InputField
            label="Bank Name"
            value={b.bankName}
            onChange={(e) => handleBankingChange('bankName', e.target.value)}
            placeholder="e.g. HDFC Bank"
          />
          <InputField
            label="Account Number"
            value={b.accountNumber}
            onChange={(e) => handleBankingChange('accountNumber', e.target.value)}
            placeholder="XXXXXXXXXXXX"
          />
          <InputField
            label="IFSC Code"
            value={b.ifscCode}
            onChange={(e) => handleBankingChange('ifscCode', e.target.value.toUpperCase())}
            placeholder="e.g. HDFC0001234"
            maxLength={11}
          />
          <InputField
            label="Branch Name"
            value={b.branchName}
            onChange={(e) => handleBankingChange('branchName', e.target.value)}
            placeholder="e.g. MG Road Branch"
          />
          <InputField
            label="UPI ID"
            value={b.upiId}
            onChange={(e) => handleBankingChange('upiId', e.target.value)}
            placeholder="example@upi"
          />
        </div>

        {/* Optional uploads — Cancelled Cheque + QR Code */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 pt-2">
          {/* Cancelled Cheque */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Cancelled Cheque <span className="text-gray-400">(or fill bank details)</span></label>
            {b.cancelledChequeUrl ? (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText size={18} className="text-red-500 shrink-0" />
                    <span className="text-sm text-gray-700 truncate">{b.cancelledChequeName || 'Cheque'}</span>
                  </div>
                  <button type="button" onClick={() => { handleBankingChange('cancelledChequeUrl', ''); handleBankingChange('cancelledChequeName', ''); }}
                    className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg" data-testid="master-cheque-remove">
                    <X size={16} />
                  </button>
                </div>
                {/* 3C — full readable cheque preview (image; PDF shown via link) */}
                {/\.(jpg|jpeg|png|webp)$/i.test(b.cancelledChequeUrl) ? (
                  <a href={b.cancelledChequeUrl} target="_blank" rel="noopener noreferrer" title="Open full size">
                    <img src={b.cancelledChequeUrl} alt="Cancelled cheque preview"
                      className="w-full max-h-72 object-contain rounded-md border border-gray-200 bg-white" data-testid="master-cheque-preview" />
                  </a>
                ) : (
                  <a href={b.cancelledChequeUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-teal-600 hover:underline">Open cheque (PDF)</a>
                )}
              </div>
            ) : (
              <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-gray-200 rounded-lg cursor-pointer hover:border-teal-400 hover:bg-teal-50/50 transition-colors">
                <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden"
                  disabled={bankUploading.cheque}
                  onChange={(e) => handleBankDocUpload('cheque', e.target.files[0])}
                  data-testid="master-cheque-upload" />
                {bankUploading.cheque ? <Loader2 size={18} className="animate-spin text-teal-600" /> : <Upload size={18} className="text-gray-400" />}
                <span className="text-sm text-gray-500">{bankUploading.cheque ? 'Uploading...' : 'Upload cheque (PDF/Image, Max 10MB)'}</span>
              </label>
            )}
          </div>

          {/* QR Code */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Payment QR Code <span className="text-red-500">*</span></label>
            {b.qrCodeUrl ? (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 min-w-0">
                    <QrCode size={18} className="text-teal-600 shrink-0" />
                    <span className="text-sm text-gray-700 truncate">{b.qrCodeName || 'QR Code'}</span>
                  </div>
                  <button type="button" onClick={() => { handleBankingChange('qrCodeUrl', ''); handleBankingChange('qrCodeName', ''); }}
                    className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg" data-testid="master-qr-remove">
                    <X size={16} />
                  </button>
                </div>
                {/* 3C — large, scannable QR preview, true aspect ratio, no distortion */}
                <a href={b.qrCodeUrl} target="_blank" rel="noopener noreferrer" title="Open full size">
                  <img src={b.qrCodeUrl} alt="Payment QR code preview"
                    className="mx-auto w-56 h-56 object-contain rounded-md border border-gray-200 bg-white p-2" data-testid="master-qr-preview" />
                </a>
                <p className="text-[11px] text-gray-400 text-center">Tap the QR to open it full size for scanning.</p>
              </div>
            ) : (
              <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-gray-200 rounded-lg cursor-pointer hover:border-teal-400 hover:bg-teal-50/50 transition-colors">
                <input type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden"
                  disabled={bankUploading.qr}
                  onChange={(e) => handleBankDocUpload('qr', e.target.files[0])}
                  data-testid="master-qr-upload" />
                {bankUploading.qr ? <Loader2 size={18} className="animate-spin text-teal-600" /> : <Upload size={18} className="text-gray-400" />}
                <span className="text-sm text-gray-500">{bankUploading.qr ? 'Uploading...' : 'Upload QR (JPG/PNG/WEBP, Max 10MB)'}</span>
              </label>
            )}
          </div>
        </div>
        {/* 3B — validation messages */}
        {errors.qrCode && <p className="text-sm text-red-600" data-testid="master-qr-error">{errors.qrCode}</p>}
        {errors.bankDetails && <p className="text-sm text-red-600" data-testid="master-bank-error">{errors.bankDetails}</p>}
      </div>
    );
  };

  const renderStep6 = () => (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 p-4 rounded-xl">
        <p className="text-sm text-blue-700">
          Please review your information below before submitting. You can go back to any step to make changes.
        </p>
      </div>

      {/* Property Info */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <Building2 size={18} className="text-teal-600" /> Property Details
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <ReviewField label="Property Name" value={formData.propertyName} />
          <ReviewField label="Property Type" value={formData.propertyType} />
          <ReviewField label="Star Rating" value={`${formData.starRating} Stars`} />
          <ReviewField label="Total Rooms" value={formData.totalRooms ? `${formData.totalRooms} Rooms` : null} />
          <ReviewField label="Owner Name" value={formData.ownerName} />
          <ReviewField label="Email" value={formData.email} />
          <ReviewField label="Mobile" value={formData.mobile} />
        </div>
      </div>

      {/* Location Info */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <MapPin size={18} className="text-teal-600" /> Location Details
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <ReviewField label="Address" value={formData.address} />
          <ReviewField label="City" value={formData.city} />
          <ReviewField label="State" value={formData.state} />
          <ReviewField label="Pin Code" value={formData.pinCode} />
        </div>
      </div>

      {/* Room Summary */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <Home size={18} className="text-teal-600" /> Room Inventory
        </h4>
        {formData.rooms.length > 0 ? (
          <div className="space-y-3">
            {formData.rooms.map((room) => {
              const roomLabel = room.category === 'Other' ? room.customRoomType : room.category;
              return (
                <div key={room.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-sm py-3 px-4 bg-gray-50 rounded-lg border border-gray-100">
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="font-medium text-gray-800 break-words">{roomLabel}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                      room.roomTypeCategory === 'AC' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'
                    }`}>{room.roomTypeCategory || 'AC'}</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-gray-600 sm:justify-end">
                    <span>{room.count} rooms</span>
                    {room.roomSize ? <span>{room.roomSize} {room.roomSizeUnit}</span> : null}
                    <span>&#8377;{room.basePrice}/night</span>
                    {room.weekendPrice ? <span>Wknd &#8377;{room.weekendPrice}</span> : null}
                    <span>{room.noOfAdults || 0} Adults · {room.noOfChildren || 0} Children</span>
                  </div>
                </div>
              );
            })}
            <div className="flex justify-between text-sm pt-2 font-medium">
              <span>Total Rooms</span>
              <span className="text-teal-600">{formData.rooms.reduce((sum, r) => sum + (parseInt(r.count) || 0), 0)}</span>
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-400">No rooms configured</p>
        )}
      </div>

      {/* Amenities Summary */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <Wifi size={18} className="text-teal-600" /> Amenities & Facilities
        </h4>
        <div className="grid grid-cols-1 gap-4">
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase mb-2">Room Amenities</p>
            {(formData.rooms || []).some(r => Object.keys(r.amenities || {}).length > 0) ? (
              <div className="space-y-2">
                {formData.rooms.map((room) => {
                  const keys = Object.keys(room.amenities || {});
                  if (keys.length === 0) return null;
                  const roomLabel = room.category === 'Other' ? (room.customRoomType || 'Custom Room') : room.category;
                  return (
                    <div key={room.id} className="flex flex-wrap items-center gap-1.5">
                      <span className="text-xs font-semibold text-gray-600 mr-1">{roomLabel}:</span>
                      {keys.map(a => {
                        const v = room.amenities[a];
                        const label = (a === 'Bathroom' && v?.variant) ? `Bathroom (${v.variant})` : a;
                        return <span key={a} className="px-2 py-1 bg-teal-50 text-teal-700 text-xs rounded-full">{label}</span>;
                      })}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-gray-400">None selected</p>
            )}
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase mb-2">Facilities</p>
            {Object.keys(formData.facilities || {}).length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(formData.facilities).map(([f, v]) => {
                  let label = f;
                  if (YES_NO_FACILITIES.includes(f)) {
                    label = `${f}: ${v?.value || '—'}`;
                    if (f === 'Pet Allowed' && v?.value === 'Yes' && v?.charge) {
                      label += v.charge === 'paid' ? ` (Paid ₹${v.amount || 0})` : ' (Free)';
                    }
                  } else if (CHARGE_FACILITIES.includes(f)) {
                    label = `${f}${v?.charge ? ` (${v.charge === 'paid' ? `Paid ₹${v.amount || 0}` : 'Free'})` : ''}`;
                    if (f === 'Swimming Pool' && v?.poolType) label += ` · ${v.poolType}`;
                  }
                  return <span key={f} className="px-2 py-1 bg-purple-50 text-purple-700 text-xs rounded-full">{label}</span>;
                })}
              </div>
            ) : (
              <p className="text-sm text-gray-400">None selected</p>
            )}
          </div>
        </div>
      </div>

      {/* Banking & Payment — 3C: same QR/cheque preview shown in review */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <CreditCard size={18} className="text-teal-600" /> Banking & Payment
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
          <ReviewField label="Account Holder" value={formData.banking?.accountHolderName} />
          <ReviewField label="Bank Name" value={formData.banking?.bankName} />
          <ReviewField label="Account Number" value={formData.banking?.accountNumber} />
          <ReviewField label="IFSC Code" value={formData.banking?.ifscCode} />
          <ReviewField label="Branch" value={formData.banking?.branchName} />
          <ReviewField label="UPI ID" value={formData.banking?.upiId} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase mb-2">Payment QR Code</p>
            {formData.banking?.qrCodeUrl ? (
              <a href={formData.banking.qrCodeUrl} target="_blank" rel="noopener noreferrer" title="Open full size">
                <img src={formData.banking.qrCodeUrl} alt="Payment QR code"
                  className="w-48 h-48 object-contain rounded-md border border-gray-200 bg-white p-2" data-testid="review-qr-preview" />
              </a>
            ) : <p className="text-sm text-gray-400">Not uploaded</p>}
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase mb-2">Cancelled Cheque</p>
            {formData.banking?.cancelledChequeUrl ? (
              /\.(jpg|jpeg|png|webp)$/i.test(formData.banking.cancelledChequeUrl) ? (
                <a href={formData.banking.cancelledChequeUrl} target="_blank" rel="noopener noreferrer" title="Open full size">
                  <img src={formData.banking.cancelledChequeUrl} alt="Cancelled cheque"
                    className="w-full max-h-56 object-contain rounded-md border border-gray-200 bg-white" data-testid="review-cheque-preview" />
                </a>
              ) : (
                <a href={formData.banking.cancelledChequeUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-teal-600 hover:underline">Open cheque (PDF)</a>
              )
            ) : <p className="text-sm text-gray-400">Not uploaded</p>}
          </div>
        </div>
      </div>
    </div>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1: return renderStep1();
      case 2: return renderStep2();
      case 3: return renderStep3();
      case 4: return renderStep4();
      case 5: return renderStep5();
      case 6: return renderStep6();
      default: return null;
    }
  };

  return (
    <div className="w-full" data-testid="multi-step-onboarding-form">
      {/* Progress Tracker */}
      <ProgressTracker currentStep={currentStep} completedSteps={completedSteps} />

      {/* Form Content */}
      <form onSubmit={(e) => e.preventDefault()}>
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 md:p-8 min-h-[400px]">
          {/* Step Title */}
          <div className="mb-6 pb-4 border-b border-gray-100">
            <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
              {React.createElement(STEPS[currentStep - 1].icon, { size: 22, className: 'text-teal-600' })}
              {STEPS[currentStep - 1].title}
            </h3>
          </div>

          {/* Step Content */}
          {renderCurrentStep()}
        </div>

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between mt-6">
          <button
            type="button"
            onClick={handleBack}
            disabled={currentStep === 1}
            className={`flex items-center px-5 py-2.5 rounded-lg font-medium transition-colors ${
              currentStep === 1
                ? 'text-gray-300 cursor-not-allowed'
                : 'text-gray-700 bg-gray-100 hover:bg-gray-200'
            }`}
          >
            <ChevronLeft size={20} className="mr-1" />
            Back
          </button>

          {currentStep < STEPS.length ? (
            <button
              type="button"
              onClick={handleNext}
              className="flex items-center px-6 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium"
            >
              Next
              <ChevronRight size={20} className="ml-1" />
            </button>
          ) : (
            <button
              type="button"
              onClick={handleFinalSubmit}
              disabled={saving}
              className="flex items-center px-6 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium disabled:opacity-50"
              data-testid="submit-property-btn"
            >
              {saving ? (
                <>
                  <Loader2 size={18} className="animate-spin mr-2" />
                  Submitting...
                </>
              ) : (
                <>
                  <Save size={18} className="mr-2" />
                  Submit
                </>
              )}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default MultiStepOnboardingForm;
